import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { FiasWebSocket } from '../../lib/websocket';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';

interface LogEntry {
  id: number;
  timestamp: Date;
  direction: 'sent' | 'received';
  type: string;
  data: string;
}

export function WebSocketTester() {
  // Connection state
  const [endpoint, setEndpoint] = useState('');
  const [authToken, setAuthToken] = useState('');
  const [connectionState, setConnectionState] = useState<
    'disconnected' | 'connecting' | 'connected' | 'reconnecting'
  >('disconnected');

  // Subscription state
  const [requestId, setRequestId] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [currentSubscription, setCurrentSubscription] = useState<string | null>(null);

  // Message log
  const [messages, setMessages] = useState<LogEntry[]>([]);
  const logIdRef = useRef(0);
  const logContainerRef = useRef<HTMLDivElement>(null);

  // Metrics
  const [metrics, setMetrics] = useState({
    messagesReceived: 0,
    tokensStreamed: 0,
    connectionDuration: 0,
    lastMessageAt: null as Date | null,
  });
  const connectedAtRef = useRef<Date | null>(null);
  const metricsIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // WebSocket ref
  const wsRef = useRef<FiasWebSocket | null>(null);

  // Add log entry
  const addLog = (direction: 'sent' | 'received', type: string, data: unknown) => {
    const entry: LogEntry = {
      id: logIdRef.current++,
      timestamp: new Date(),
      direction,
      type,
      data: typeof data === 'object' ? JSON.stringify(data, null, 2) : String(data),
    };
    setMessages((prev) => [...prev, entry].slice(-500)); // Keep last 500 messages
  };

  // Auto-scroll log
  useEffect(() => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [messages]);

  // Update connection duration
  useEffect(() => {
    if (connectionState === 'connected' && !metricsIntervalRef.current) {
      connectedAtRef.current = new Date();
      metricsIntervalRef.current = setInterval(() => {
        if (connectedAtRef.current) {
          setMetrics((prev) => ({
            ...prev,
            connectionDuration: Math.floor((Date.now() - connectedAtRef.current!.getTime()) / 1000),
          }));
        }
      }, 1000);
    } else if (connectionState !== 'connected' && metricsIntervalRef.current) {
      clearInterval(metricsIntervalRef.current);
      metricsIntervalRef.current = null;
      connectedAtRef.current = null;
    }

    return () => {
      if (metricsIntervalRef.current) {
        clearInterval(metricsIntervalRef.current);
      }
    };
  }, [connectionState]);

  // Connect handler
  const handleConnect = async () => {
    if (!endpoint || !authToken) {
      addLog('received', 'error', 'Endpoint and auth token are required');
      return;
    }

    // Cleanup existing connection
    if (wsRef.current) {
      wsRef.current.disconnect();
    }

    // Create new WebSocket
    wsRef.current = new FiasWebSocket({ endpoint });

    // Set up event handlers
    wsRef.current.onConnectionChange((state) => {
      setConnectionState(state);
      addLog('received', 'connection_state', state);
    });

    wsRef.current.onToken((nodeId, text) => {
      addLog('received', 'token', { nodeId, text });
      setMetrics((prev) => ({
        ...prev,
        messagesReceived: prev.messagesReceived + 1,
        tokensStreamed: prev.tokensStreamed + 1,
        lastMessageAt: new Date(),
      }));
    });

    wsRef.current.onNodeComplete((nodeId) => {
      addLog('received', 'node_complete', { nodeId });
      setMetrics((prev) => ({
        ...prev,
        messagesReceived: prev.messagesReceived + 1,
        lastMessageAt: new Date(),
      }));
    });

    wsRef.current.onComplete((completedRequestId) => {
      addLog('received', 'complete', { requestId: completedRequestId });
      setMetrics((prev) => ({
        ...prev,
        messagesReceived: prev.messagesReceived + 1,
        lastMessageAt: new Date(),
      }));
    });

    wsRef.current.onError((code, message) => {
      addLog('received', 'error', { code, message });
      setMetrics((prev) => ({
        ...prev,
        messagesReceived: prev.messagesReceived + 1,
        lastMessageAt: new Date(),
      }));
    });

    wsRef.current.onSubscribed((subscribedRequestId) => {
      addLog('received', 'subscribed', { requestId: subscribedRequestId });
      setIsSubscribed(true);
      setCurrentSubscription(subscribedRequestId);
    });

    try {
      addLog('sent', 'connect', { endpoint });
      await wsRef.current.connect(authToken);
    } catch (error) {
      addLog('received', 'error', {
        code: 'CONNECTION_FAILED',
        message: error instanceof Error ? error.message : 'Failed to connect',
      });
    }
  };

  // Disconnect handler
  const handleDisconnect = () => {
    if (wsRef.current) {
      addLog('sent', 'disconnect', {});
      wsRef.current.disconnect();
      wsRef.current = null;
      setIsSubscribed(false);
      setCurrentSubscription(null);
    }
  };

  // Subscribe handler
  const handleSubscribe = () => {
    if (!requestId || !wsRef.current || connectionState !== 'connected') {
      return;
    }
    addLog('sent', 'subscribe', { requestId });
    wsRef.current.subscribe(requestId);
  };

  // Unsubscribe handler
  const handleUnsubscribe = () => {
    if (!wsRef.current || connectionState !== 'connected') {
      return;
    }
    addLog('sent', 'unsubscribe', {});
    wsRef.current.unsubscribe();
    setIsSubscribed(false);
    setCurrentSubscription(null);
  };

  // Send ping
  const handlePing = () => {
    if (!wsRef.current || connectionState !== 'connected') {
      return;
    }
    // Ping is sent automatically, but we can trigger it manually
    addLog('sent', 'ping', {});
  };

  // Clear log
  const handleClearLog = () => {
    setMessages([]);
    logIdRef.current = 0;
  };

  // Reset metrics
  const handleResetMetrics = () => {
    setMetrics({
      messagesReceived: 0,
      tokensStreamed: 0,
      connectionDuration: 0,
      lastMessageAt: null,
    });
    connectedAtRef.current = connectionState === 'connected' ? new Date() : null;
  };

  // Format duration
  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  // Format time since
  const formatTimeSince = (date: Date | null): string => {
    if (!date) return 'Never';
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    return `${Math.floor(seconds / 60)}m ago`;
  };

  // Connection status color
  const statusColor = {
    disconnected: 'bg-muted-foreground',
    connecting: 'bg-yellow-400',
    connected: 'bg-green-400',
    reconnecting: 'bg-yellow-400',
  }[connectionState];

  return (
    <div>
      {/* Page Header */}
      <div className="bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Link to="/dev-console">
                <Button variant="ghost" size="icon">
                  <ChevronLeft className="h-6 w-6" />
                </Button>
              </Link>
              <h1 className="text-xl font-bold">WebSocket Tester</h1>
            </div>
            <div className="flex items-center gap-2">
              <span className={cn("inline-block w-3 h-3 rounded-full", statusColor)} />
              <span className="text-sm text-muted-foreground capitalize">{connectionState}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left panel: Connection & Subscription */}
          <div className="space-y-6">
            {/* Connection Setup */}
            <Card>
              <CardHeader>
                <CardTitle>Connection</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>WebSocket Endpoint</Label>
                  <Input
                    type="text"
                    value={endpoint}
                    onChange={(e) => setEndpoint(e.target.value)}
                    placeholder="wss://api.example.com/stream"
                    className="mt-1"
                    disabled={connectionState !== 'disconnected'}
                  />
                </div>

                <div>
                  <Label>Auth Token (JWT)</Label>
                  <Textarea
                    value={authToken}
                    onChange={(e) => setAuthToken(e.target.value)}
                    placeholder="eyJhbGciOiJIUzI1NiIs..."
                    className="mt-1 h-20 font-mono text-xs"
                    disabled={connectionState !== 'disconnected'}
                  />
                </div>

                <div className="flex gap-2">
                  {connectionState === 'disconnected' ? (
                    <Button
                      onClick={handleConnect}
                      disabled={!endpoint || !authToken}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                    >
                      Connect
                    </Button>
                  ) : (
                    <Button
                      variant="destructive"
                      onClick={handleDisconnect}
                      className="flex-1"
                    >
                      Disconnect
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Subscription */}
            <Card>
              <CardHeader>
                <CardTitle>Subscription</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Request ID</Label>
                  <Input
                    type="text"
                    value={requestId}
                    onChange={(e) => setRequestId(e.target.value)}
                    placeholder="req_abc123..."
                    className="mt-1 font-mono"
                    disabled={connectionState !== 'connected'}
                  />
                </div>

                {currentSubscription && (
                  <div className="text-sm text-muted-foreground">
                    Subscribed to: <code className="bg-muted px-1 rounded">{currentSubscription}</code>
                  </div>
                )}

                <div className="flex gap-2">
                  <Button
                    onClick={handleSubscribe}
                    disabled={connectionState !== 'connected' || !requestId || isSubscribed}
                    className="flex-1"
                  >
                    Subscribe
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={handleUnsubscribe}
                    disabled={connectionState !== 'connected' || !isSubscribed}
                    className="flex-1"
                  >
                    Unsubscribe
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Actions */}
            <Card>
              <CardHeader>
                <CardTitle>Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button
                  variant="secondary"
                  onClick={handlePing}
                  disabled={connectionState !== 'connected'}
                  className="w-full"
                >
                  Send Ping
                </Button>
                <Button
                  variant="secondary"
                  onClick={handleClearLog}
                  className="w-full"
                >
                  Clear Log
                </Button>
                <Button
                  variant="secondary"
                  onClick={handleResetMetrics}
                  className="w-full"
                >
                  Reset Metrics
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Middle panel: Message Stream */}
          <div className="lg:col-span-2 space-y-6">
            {/* Message Stream */}
            <Card>
              <CardHeader>
                <CardTitle>Message Stream</CardTitle>
              </CardHeader>
              <div
                ref={logContainerRef}
                className="h-96 overflow-y-auto p-4 font-mono text-sm bg-muted/30"
              >
                {messages.length === 0 ? (
                  <div className="text-muted-foreground text-center py-8">
                    No messages yet. Connect and subscribe to see streaming tokens.
                  </div>
                ) : (
                  messages.map((entry) => (
                    <div
                      key={entry.id}
                      className={cn(
                        "mb-2 p-2 rounded",
                        entry.direction === 'sent'
                          ? 'bg-primary/10 border-l-4 border-primary'
                          : entry.type === 'error'
                            ? 'bg-destructive/10 border-l-4 border-destructive'
                            : 'bg-green-500/10 border-l-4 border-green-500'
                      )}
                    >
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                        <span>{entry.direction === 'sent' ? '->' : '<-'}</span>
                        <span className="font-medium">{entry.type}</span>
                        <span className="ml-auto">
                          {entry.timestamp.toLocaleTimeString()}
                        </span>
                      </div>
                      <pre className="text-xs whitespace-pre-wrap break-all">{entry.data}</pre>
                    </div>
                  ))
                )}
              </div>
            </Card>

            {/* Metrics */}
            <Card>
              <CardHeader>
                <CardTitle>Metrics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <div className="text-2xl font-bold">
                      {metrics.messagesReceived}
                    </div>
                    <div className="text-sm text-muted-foreground">Messages Received</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold">
                      {metrics.tokensStreamed.toLocaleString()}
                    </div>
                    <div className="text-sm text-muted-foreground">Tokens Streamed</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold">
                      {formatDuration(metrics.connectionDuration)}
                    </div>
                    <div className="text-sm text-muted-foreground">Connection Duration</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold">
                      {formatTimeSince(metrics.lastMessageAt)}
                    </div>
                    <div className="text-sm text-muted-foreground">Last Message</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WebSocketTester;
